import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/brand";
import {
  Gift,
  Copy,
  CheckCircle,
  Users,
  Envelope,
  Sparkle,
  ArrowClockwise,
  ShareNetwork,
} from "@phosphor-icons/react";

// Iter39 — Refer-a-friend. Both parties get a 30-day billing-cycle-anchor
// shift on the referee's first successful invoice. Reward mechanism lives
// in routes/referrals.py (backend) so this page is purely a share-and-track UI.
export default function ReferralsPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [meRes, histRes] = await Promise.all([
        axios.get(`${API}/referrals/me`),
        axios.get(`${API}/referrals/history`),
      ]);
      setStats(meRes.data);
      setHistory(histRes.data?.referrals || []);
    } catch (e) {
      toast.error("Could not load your referral link");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const copyLink = () => {
    if (!stats?.share_url) return;
    navigator.clipboard.writeText(stats.share_url).then(() => {
      setCopied(true);
      toast.success("Share link copied");
      setTimeout(() => setCopied(false), 2500);
    });
  };

  // Iter38c — native mobile share sheet via Web Share API. Opens WhatsApp /
  // iMessage / Instagram DM / Signal / etc. directly on iOS + Android; on
  // desktop Chrome/Firefox the button hides (canShare === false) so it
  // stays a mobile-first affordance. Feature-detected at render time.
  const canNativeShare =
    typeof navigator !== "undefined" &&
    typeof navigator.share === "function" &&
    !!stats?.share_url;

  const nativeShare = async () => {
    if (!stats?.share_url) return;
    const firstName = (user?.name || "").split(" ")[0] || "me";
    try {
      await navigator.share({
        title: "forecastly — 12-month cash-flow clarity",
        text: `${firstName} thought you'd get on with forecastly — a friendly little tool for keeping on top of your cash flow. Sign up with my link and you'll get a whole extra month free on us:`,
        url: stats.share_url,
      });
      // Some browsers resolve the promise even on cancel; toast is optional
      // so we skip firing one here to avoid a false-positive "shared!".
    } catch (err) {
      // User cancelled the share sheet — no-op, this is expected.
      if (err?.name !== "AbortError") {
        toast.error("Couldn't open the share sheet on this device.");
      }
    }
  };

  const mailtoLink = () => {
    if (!stats?.share_url) return "#";
    const subject = encodeURIComponent(
      "I've been using forecastly — thought you'd like it too",
    );
    const body = encodeURIComponent(
      `Hey,\n\nI've been using forecastly to keep on top of my cash flow — it's honestly the first tool that's made me actually look forward to checking in on the numbers.\n\nSigning up with my link gets us both an extra free month once you go past the trial:\n\n${stats.share_url}\n\nNo pressure — but if the "will there be enough money next month?" question is ever on your mind, this one's worth a try.\n\n${(user?.name || "").split(" ")[0] || "Me"}`,
    );
    return `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <div className="space-y-6" data-testid="referrals-page">
      {/* Header */}
      <div>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="section-eyebrow flex items-center gap-2">
              <Gift size={16} weight="bold" /> Refer a friend
            </div>
            <h1 className="font-heading uppercase text-4xl mt-2">
              Give a month · get a month
            </h1>
          </div>
          <button
            type="button"
            className="btn-secondary shrink-0 mt-1"
            onClick={load}
            disabled={loading}
            data-testid="referrals-reload"
          >
            <ArrowClockwise size={14} weight="bold" />{" "}
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
        <p className="text-black/75 mt-3 leading-relaxed max-w-2xl">
          Share forecastly with a fellow business-owner friend. They get a
          free month on us — added to their standard trial, so they&apos;ve got
          plenty of time to fall in love with their forecast. When they
          decide to stay and make their first payment, your next month is on
          us too. No codes. No coupons. No awkwardness.
        </p>
      </div>

      {/* Share card */}
      <div
        className="card border-2 border-black bg-[#FFF7E5]"
        data-testid="referrals-share-card"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <div className="text-xs uppercase tracking-widest font-semibold text-black/60">
              Your personal link
            </div>
            <div className="mt-2 font-mono text-sm break-all" data-testid="referral-share-url">
              {stats?.share_url || "—"}
            </div>
            <div className="mt-1 text-[11px] text-black/50">
              Code: <span className="font-mono font-semibold">{stats?.code || "—"}</span>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={copyLink}
              className="btn-primary"
              disabled={!stats?.share_url}
              data-testid="referrals-copy-btn"
            >
              {copied ? (
                <>
                  <CheckCircle size={14} weight="fill" /> Copied
                </>
              ) : (
                <>
                  <Copy size={14} weight="bold" /> Copy link
                </>
              )}
            </button>
            {canNativeShare && (
              <button
                type="button"
                onClick={nativeShare}
                className="btn-primary"
                data-testid="referrals-share-btn"
                title="Share via WhatsApp, iMessage, and more"
              >
                <ShareNetwork size={14} weight="bold" /> Share
              </button>
            )}
            <a
              href={mailtoLink()}
              className="btn-secondary"
              data-testid="referrals-email-btn"
            >
              <Envelope size={14} weight="bold" /> Email a friend
            </a>
          </div>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-3 gap-3" data-testid="referrals-stats">
        <StatBox
          icon={<Users size={16} weight="bold" />}
          label="Invited"
          value={stats?.invited_count ?? 0}
        />
        <StatBox
          icon={<Sparkle size={16} weight="bold" />}
          label="Rewarded"
          value={stats?.rewarded_count ?? 0}
          accent
        />
        <StatBox
          icon={<Gift size={16} weight="bold" />}
          label="Pending"
          value={stats?.pending_count ?? 0}
        />
      </div>

      {/* History */}
      <div className="card" data-testid="referrals-history">
        <h2 className="font-heading uppercase text-xl">Recent invites</h2>
        {history.length === 0 ? (
          <p className="text-sm text-black/60 mt-3">
            No invites yet. Share your link — your first month free is one referral
            away.
          </p>
        ) : (
          <ul className="mt-4 divide-y divide-black/10">
            {history.map((r, i) => (
              <li
                key={i}
                className="py-3 flex items-center justify-between gap-3 flex-wrap"
                data-testid={`referral-row-${i}`}
              >
                <div>
                  <div className="text-sm font-semibold">{r.referee_email}</div>
                  <div className="text-[11px] text-black/50">
                    Invited{" "}
                    {r.created_at
                      ? new Date(r.created_at).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                      : "—"}
                    {r.rewarded_at && (
                      <>
                        {" · Rewarded "}
                        {new Date(r.rewarded_at).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </>
                    )}
                  </div>
                </div>
                <span
                  className={`text-[10px] uppercase tracking-widest font-heading px-2 py-1 rounded border-[1.5px] ${
                    r.status === "rewarded"
                      ? "bg-emerald-100 border-emerald-700 text-emerald-900"
                      : "bg-[#FFBD4A] border-black"
                  }`}
                >
                  {r.status}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Fine print — clear T&Cs so users understand what triggers rewards */}
      <div className="card !bg-black/5 !border-black/15" data-testid="referrals-terms">
        <h3 className="text-xs uppercase tracking-widest font-semibold text-black/70">
          How it works — the fine print
        </h3>
        <ul className="mt-3 text-xs text-black/70 space-y-2 leading-relaxed list-disc pl-5">
          <li>
            Your friend gets a <strong>free month added to their standard
            trial</strong> — that&apos;s 40 days total to try forecastly before
            their first payment. They can choose Monthly (£19/mo) or Annual
            (£190/yr).
          </li>
          <li>
            You earn your free month <strong>only after your friend&apos;s
            first successful payment</strong> lands — not when they first sign
            up. Their trial extension is our gift to them regardless.
          </li>
          <li>
            If your friend decides to cancel during their extended trial,
            they&apos;re never charged and no reward triggers. Fair for
            everyone.
          </li>
          <li>
            Your free month is applied as a <strong>30-day billing cycle
            extension</strong> — your next payment date simply moves forward
            by 30 days. No invoices, no credits, no confusion.
          </li>
          <li>
            One reward per unique friend. Self-referrals don&apos;t count.
            Invite as many people as you like — stack those free months up.
          </li>
          <li>
            <strong>Multiple successful referrals extend your free months
            consecutively</strong> — two friends = two free months back to
            back, three friends = three free months, and so on.
          </li>
          <li>
            Rewards can&apos;t be exchanged for cash and can&apos;t be
            combined with other active promotions on the same subscription.
          </li>
        </ul>
      </div>
    </div>
  );
}

function StatBox({ icon, label, value, accent }) {
  return (
    <div
      className={`card !py-3 !px-4 ${accent ? "bg-[#FFF7E5] border-2" : ""}`}
    >
      <div className="text-[10px] uppercase tracking-widest text-black/55 font-semibold inline-flex items-center gap-1">
        {icon} {label}
      </div>
      <div className="text-3xl font-heading mt-1">{value}</div>
    </div>
  );
}
